import { useState, useRef, useEffect, KeyboardEvent } from 'react';

import { UserV2 } from '../../../../../backend/src/user/user.entity';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/new/components/shadcn/command';
import { Popover, PopoverContent, PopoverAnchor } from '@/new/components/shadcn/popover';
import { cn } from '@/new/lib/utils';

interface MentionsInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  users?: UserV2[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function MentionsInput({
  value,
  onChange,
  onSubmit,
  users = [],
  placeholder = 'Add a comment...',
  className,
  disabled = false,
}: MentionsInputProps) {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const editorRef = useRef<HTMLDivElement>(null);

  // Filter users based on search
  const filteredUsers = users?.filter((user) => user.email.toLowerCase().includes(mentionSearch.toLowerCase())) || [];

  // Reset selected index when filtered users change
  useEffect(() => {
    setSelectedIndex(0);
  }, [mentionSearch]);

  // Convert internal value format (@[email](userId)) to display HTML
  const valueToHTML = (val: string): string => {
    if (!val) return '';

    // Escape HTML in plain text parts
    const escapeHTML = (str: string) => {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };

    // Replace @[email](userId) with styled spans
    const mentionRegex = /@\[(.*?)\]\((.*?)\)/g;
    let lastIndex = 0;
    let result = '';

    let match;
    while ((match = mentionRegex.exec(val)) !== null) {
      // Add escaped text before mention
      result += escapeHTML(val.slice(lastIndex, match.index));

      // Add mention span
      const email = match[1];
      const userId = match[2];
      result += `<span class="mention" data-user-id="${userId}" data-email="${email}" contenteditable="false"><span class="text-teal-600">@${escapeHTML(email)}</span></span>`;

      lastIndex = match.index + match[0].length;
    }

    // Add remaining escaped text
    result += escapeHTML(val.slice(lastIndex));

    // Replace newlines with <br>
    result = result.replace(/\n/g, '<br>');

    return result;
  };

  // Convert display HTML back to internal format
  const htmlToValue = (html: string): string => {
    // Create a temporary div to parse HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Replace mention spans with @[email](userId) format
    const mentions = temp.querySelectorAll('.mention');
    mentions.forEach((mention) => {
      const email = mention.getAttribute('data-email');
      const userId = mention.getAttribute('data-user-id');
      const textNode = document.createTextNode(`@[${email}](${userId})`);
      mention.replaceWith(textNode);
    });

    // Replace <br> with newlines
    temp.querySelectorAll('br').forEach((br) => {
      br.replaceWith(document.createTextNode('\n'));
    });

    return temp.textContent || '';
  };

  // Get cursor position in text content (visible text)
  const getCursorPosition = (): number => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current) return 0;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editorRef.current);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    return preCaretRange.toString().length;
  };

  // Convert cursor position from visible text to internal value format
  const mapVisiblePosToValuePos = (visiblePos: number, visibleText: string, internalValue: string): number => {
    // Find all mentions in the internal value
    const mentionRegex = /@\[(.*?)\]\((.*?)\)/g;
    let valuePos = 0;
    let currentVisiblePos = 0;
    let match;
    let lastIndex = 0;

    while ((match = mentionRegex.exec(internalValue)) !== null) {
      const mentionStart = match.index;
      const mentionEnd = match.index + match[0].length;
      const email = match[1];

      // Add characters before this mention
      const beforeMention = internalValue.slice(lastIndex, mentionStart);
      const beforeLength = beforeMention.length;

      if (currentVisiblePos + beforeLength >= visiblePos) {
        // Cursor is before this mention
        return valuePos + (visiblePos - currentVisiblePos);
      }

      valuePos += beforeLength;
      currentVisiblePos += beforeLength;

      // This mention appears as @email in visible text
      const visibleMentionLength = email.length + 1; // +1 for @

      if (currentVisiblePos + visibleMentionLength >= visiblePos) {
        // Cursor is within or right after this mention in visible text
        // Place it after the mention in value format
        return mentionEnd;
      }

      valuePos = mentionEnd;
      currentVisiblePos += visibleMentionLength;
      lastIndex = mentionEnd;
    }

    // Cursor is after all mentions
    const remaining = internalValue.slice(lastIndex);
    return valuePos + (visiblePos - currentVisiblePos);
  };

  // Set cursor position
  const setCursorPosition = (pos: number) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    const range = document.createRange();

    let currentPos = 0;
    let targetNode: Node | null = null;
    let targetOffset = 0;

    const walker = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT);

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const textLength = node.textContent?.length || 0;

      if (currentPos + textLength >= pos) {
        targetNode = node;
        targetOffset = pos - currentPos;
        break;
      }

      currentPos += textLength;
    }

    if (targetNode) {
      range.setStart(targetNode, targetOffset);
      range.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  // Handle input changes
  const handleInput = () => {
    if (!editorRef.current) return;

    const html = editorRef.current.innerHTML;
    const textValue = htmlToValue(html);
    const visibleCursorPos = getCursorPosition();
    const visibleText = editorRef.current.textContent || '';

    onChange(textValue);

    // Check for @ trigger - use the internal value format, not textContent
    // This ensures we detect @ correctly even after mentions
    const textBeforeCursor = textValue.slice(0, textValue.length) || '';
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';

      // Check if this @ is part of a mention format @[email](userId)
      const afterAt = textBeforeCursor.slice(lastAtIndex);
      const isMentionFormat = /^@\[.*?\]\(.*?\)/.test(afterAt);

      // Check if char before @ is a whitespace character (space, non-breaking space, or newline)
      const isWhitespaceBefore = charBeforeAt === ' ' || charBeforeAt === '\u00A0' || charBeforeAt === '\n';

      // Only trigger mention dropdown if @ is not part of existing mention format
      if (!isMentionFormat && (isWhitespaceBefore || lastAtIndex === 0)) {
        const searchText = textBeforeCursor.slice(lastAtIndex + 1);
        if (!searchText.includes(' ') && !searchText.includes('\n')) {
          // Convert the @ position in visible text to value position
          const valueAtPos = mapVisiblePosToValuePos(
            visibleText.lastIndexOf('@', visibleCursorPos),
            visibleText,
            textValue,
          );
          setMentionSearch(searchText);
          setMentionStartIndex(valueAtPos);
          setShowMentions(true);
          return;
        }
      }
    }

    setShowMentions(false);
  };

  // Insert mention
  const insertMention = (user: UserV2) => {
    if (!editorRef.current || mentionStartIndex === null) return;

    // Close popover immediately
    setShowMentions(false);
    setMentionStartIndex(null);

    // wait a sec before resetting the search to prevent flash of whole list
    setTimeout(() => {
      setMentionSearch('');
    }, 150);

    // Get current state
    const visibleCursorPos = getCursorPosition();
    const visibleText = editorRef.current.textContent || '';

    // Map the visible cursor position to the internal value position
    const valueCursorPos = mapVisiblePosToValuePos(visibleCursorPos, visibleText, value);

    // Insert the mention in the internal value format
    const before = value.slice(0, mentionStartIndex);
    const after = value.slice(valueCursorPos);
    const newValue = `${before}@[${user.email}](${user.id}) ${after}`;

    onChange(newValue);

    // Update display and set cursor
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = valueToHTML(newValue);

        // Calculate visible length of 'before' text by converting mentions to their display form
        // Mentions in format @[email](userId) display as @email
        let visibleBeforeLength = 0;
        const mentionRegex = /@\[(.*?)\]\((.*?)\)/g;
        let lastIndex = 0;
        let match;

        while ((match = mentionRegex.exec(before)) !== null) {
          // Add text before this mention
          visibleBeforeLength += match.index - lastIndex;
          // Add mention display length (@email)
          visibleBeforeLength += match[1].length + 1; // +1 for @
          lastIndex = match.index + match[0].length;
        }
        // Add remaining text after last mention
        visibleBeforeLength += before.length - lastIndex;

        // Position cursor after the inserted mention (in visible text)
        const newVisibleCursorPos = visibleBeforeLength + user.email.length + 2; // +2 for @ and space
        setCursorPosition(newVisibleCursorPos);
        editorRef.current.focus();
      }
    }, 0);
  };

  // Handle backspace to delete entire mention
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (showMentions && filteredUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredUsers.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredUsers.length) % filteredUsers.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredUsers[selectedIndex]);
      } else if (e.key === 'Escape') {
        setShowMentions(false);
      }
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      editorRef.current?.blur();
      onSubmit?.();
    } else if (e.key === 'Backspace') {
      // Check if cursor is right after a mention
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (range.collapsed && range.startOffset === 0) {
          const previousNode = range.startContainer.previousSibling;

          // If previous sibling is a mention span, delete it entirely
          if (previousNode && (previousNode as Element).classList?.contains('mention')) {
            e.preventDefault();
            previousNode.remove();
            handleInput();
          }
        }
      }
    }
  };

  // Check if editor is empty (for placeholder display)
  const isEmpty = !value || value.trim() === '';

  // Update display when value changes externally
  useEffect(() => {
    if (editorRef.current) {
      const currentHTML = editorRef.current.innerHTML;
      const expectedHTML = valueToHTML(value);

      // Only update if HTML is different to avoid cursor jumping
      if (currentHTML !== expectedHTML) {
        const cursorPos = getCursorPosition();
        editorRef.current.innerHTML = expectedHTML;

        // Restore cursor position if editor still has focus
        if (document.activeElement === editorRef.current) {
          setCursorPosition(cursorPos);
        }
      }
    }
  }, [value]);

  return (
    <Popover open={showMentions && filteredUsers.length > 0}>
      <PopoverAnchor asChild className="relative">
        <div
          ref={editorRef}
          contentEditable={!disabled}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          data-placeholder={placeholder}
          suppressContentEditableWarning
          className={cn(
            'w-full rounded-md border bg-white px-3 py-2 text-xs ring-offset-2 transition-colors duration-150 before:absolute focus-visible:ring-1 focus-visible:ring-teal-500 focus-visible:outline-none',
            isEmpty && 'before:text-gray-400/80 before:content-[attr(data-placeholder)]',
            disabled && 'opacity-50',
            className,
          )}
        />
      </PopoverAnchor>
      <PopoverContent className="w-[300px] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command>
          <CommandList>
            <CommandEmpty>No users found</CommandEmpty>
            <CommandGroup>
              {filteredUsers.map((user, index) => (
                <CommandItem
                  key={user.id}
                  value={user.email}
                  onSelect={() => insertMention(user)}
                  className={cn('cursor-pointer text-gray-600', index === selectedIndex && 'bg-gray-50 text-gray-900')}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{user.email}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
